/**
 * Order Management Dashboard
 * Industrial-teal aesthetic with KPIs, tonnage hero, priority chart and recent orders.
 *
 * TypeScript since ST-00227 (was index.js). Audit batch ORD:
 *   ORD-12 Create Order only for roles that may manage orders.
 *   ORD-15 The tonnage hero is the server's `totals.ordered_tonnage` (cancelled orders are
 *          not counted) and says so; recent orders are the server's last 10.
 *   ORD-16 The "Base Layer Sync" panel (hard-coded 24/18/3/1, buttons that only logged) and
 *          the "vs last week" trends (constants 5 / -2 / 8) are gone.
 */

import { useState, useEffect, useCallback, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Clock,
  CheckCircle,
  XCircle,
  Pause,
  Activity,
  ArrowRight,
  Plus,
  List,
  BarChart3,
} from 'lucide-react';
import orderService from '../../../../services/orderService';
import useOrderSubscription from '../../../../hooks/useOrderSubscription';
import { StatusBadge, KPISkeleton, TableSkeleton, Skeleton } from '../shared';
import { PageHeader, useAuth } from '../../../../types/commonComponents';
import { formatAppDate } from '../../../../utils/dateUtils';
import { apiErrorMessage, orderPath, ORDERS_MANAGE } from '../shared/orderHelpers';
import type {
  OrderDashboardResponse,
  OrderRow,
} from '../../../../types/clientPages/orderManagement';

const MONO = { fontFamily: 'IBM Plex Mono, monospace' };
const SANS = { fontFamily: 'DM Sans, sans-serif' };

/* ─── KPI Card ─────────────────────────────── */
interface KPICardProps {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: number;
  accentColor: string;
  delay?: number;
}

const KPICard = ({ icon: Icon, label, value, accentColor, delay = 0 }: KPICardProps) => (
  <div
    className="bg-surface rounded-xl border border-slate-200 p-5 hover:shadow-card-hover transition-all duration-300 animate-fadeIn"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2">
          {label}
        </p>
        <p className="text-3xl font-bold text-slate-900 tabular-nums" style={MONO}>
          {value.toLocaleString()}
        </p>
      </div>
      <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${accentColor}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </div>
);

/* ─── Tonnage Hero Card (ORD-15) ───────────── */
const TonnageCard = ({
  orderedTonnage,
  openTonnage,
  delay = 0,
}: {
  orderedTonnage: number;
  openTonnage: number | null;
  delay?: number;
}) => (
  <div
    className="relative rounded-xl overflow-hidden animate-fadeIn h-full"
    style={{ animationDelay: `${delay}ms` }}
    data-testid="tonnage-hero"
  >
    {/* Gradient background */}
    <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-800 to-primary-900 dark:from-slate-100 dark:via-slate-100 dark:to-primary-50" />
    {/* Subtle grid pattern overlay */}
    <div
      className="absolute inset-0 opacity-[0.04]"
      style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
        backgroundSize: '24px 24px',
      }}
    />
    <div className="relative px-6 py-6 flex items-center justify-between">
      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2">
          Total Tonnage Ordered
        </p>
        <p className="text-5xl font-bold text-white tabular-nums tracking-tight" style={MONO}>
          {orderedTonnage.toLocaleString()}
        </p>
        <p className="text-sm text-primary-400 mt-1 font-medium">Metric Tonnes</p>
        <p className="text-xs text-slate-400 mt-2">
          Cancelled orders are not counted.
          {openTonnage !== null && <> Still open: {openTonnage.toLocaleString()} MT.</>}
        </p>
      </div>
      <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-primary-500/15 border border-primary-500/20">
        <Activity className="w-7 h-7 text-primary-400" />
      </div>
    </div>
  </div>
);

/* ─── Priority Chart ──────────────────────── */
interface PriorityDatum {
  priority: string;
  count: number;
}

const BAR_COLORS: Record<string, string> = {
  Urgent: 'from-red-500 to-red-400',
  High: 'from-orange-500 to-orange-400',
  Normal: 'from-primary-500 to-primary-400',
  Low: 'from-slate-400 to-slate-300',
};

const PriorityChart = ({
  priorityData,
  delay = 0,
}: {
  priorityData: PriorityDatum[];
  delay?: number;
}) => {
  const total = priorityData.reduce((sum, item) => sum + item.count, 0);
  return (
    <div
      className="bg-surface rounded-xl border border-slate-200 p-5 animate-fadeIn h-full"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-4 h-4 text-slate-400" />
        <h3 className="text-sm font-bold text-slate-800" style={SANS}>
          Priority Breakdown
        </h3>
        <span className="text-[11px] text-slate-400">(open orders)</span>
      </div>
      {priorityData.length === 0 ? (
        <p className="text-xs text-slate-400">No open orders.</p>
      ) : (
        <div className="space-y-3">
          {priorityData.map((item, index) => {
            const pct = total > 0 ? (item.count / total) * 100 : 0;
            return (
              <div
                key={item.priority}
                className="animate-slideUp"
                style={{ animationDelay: `${delay + index * 60}ms` }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-slate-600">{item.priority}</span>
                  <span className="text-xs font-bold text-slate-800 tabular-nums" style={MONO}>
                    {item.count}
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full bg-gradient-to-r ${BAR_COLORS[item.priority] || 'from-slate-400 to-slate-300'} transition-all duration-700 ease-out`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ─── Recent Orders Table ─────────────────── */
const PRIORITY_TEXT: Record<string, string> = {
  Urgent: 'text-red-600',
  High: 'text-orange-600',
  Normal: 'text-primary-600',
};

const RecentOrdersTable = ({
  orders,
  onViewOrder,
  delay = 0,
}: {
  orders: OrderRow[];
  onViewOrder: (orderNo: string | 'all') => void;
  delay?: number;
}) => (
  <div
    className="bg-surface rounded-xl border border-slate-200 overflow-hidden animate-fadeIn"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
      <h3 className="text-sm font-bold text-slate-800" style={SANS}>
        Recent Orders
      </h3>
      <button
        onClick={() => onViewOrder('all')}
        className="text-xs font-semibold text-primary-600 hover:text-primary-700 flex items-center gap-1 transition-colors"
      >
        View All
        <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>

    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-slate-50/80">
            {['Order No', 'Size', 'Status', 'Priority', 'Route', 'Created'].map((h) => (
              <th
                key={h}
                className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {orders.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-5 py-12 text-center text-sm text-slate-400">
                No recent orders found
              </td>
            </tr>
          ) : (
            orders.map((order, i) => (
              <tr
                key={order.order_no}
                onClick={() => onViewOrder(order.order_no)}
                className="hover:bg-primary-50/40 cursor-pointer transition-colors animate-slideUp"
                style={{ animationDelay: `${delay + 50 + i * 40}ms` }}
              >
                <td className="px-5 py-3.5">
                  <span className="text-sm font-bold text-slate-900" style={MONO}>
                    {order.order_no}
                  </span>
                </td>
                <td className="px-5 py-3.5">
                  <span className="text-sm text-slate-700 tabular-nums" style={MONO}>
                    {order.order_size?.toLocaleString()} MT
                  </span>
                </td>
                <td className="px-5 py-3.5">
                  <StatusBadge status={order.order_status} />
                </td>
                <td className="px-5 py-3.5">
                  <span
                    className={`text-xs font-semibold ${PRIORITY_TEXT[order.order_priority] || 'text-slate-500'}`}
                  >
                    {order.order_priority}
                  </span>
                </td>
                <td className="px-5 py-3.5">
                  <span className="text-xs text-slate-600 max-w-[180px] truncate block">
                    {order.dispatch_location_name} → {order.receipt_location_name}
                  </span>
                </td>
                <td className="px-5 py-3.5">
                  <span className="text-xs text-slate-400 tabular-nums" style={MONO}>
                    {formatAppDate(order.creation_date_time)}
                  </span>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════
   MAIN DASHBOARD COMPONENT
   ═══════════════════════════════════════════════ */
const OrderDashboard = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(ORDERS_MANAGE);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<OrderDashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await orderService.getDashboardData();
      setDashboardData(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError(apiErrorMessage(err, 'Failed to load dashboard data. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useOrderSubscription({
    onOrderCreated: () => fetchDashboardData(),
    onOrderUpdated: () => fetchDashboardData(),
    onStatusChanged: () => fetchDashboardData(),
    onDashboardRefresh: () => fetchDashboardData(),
  });

  const handleViewOrder = (orderNo: string | 'all') => {
    if (orderNo === 'all') {
      navigate('/order-management/orders');
    } else {
      navigate(orderPath(orderNo));
    }
  };

  /* ─── Loading State ─── */
  if (loading && !dashboardData) {
    return (
      <div className="p-6 lg:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-56 mb-2" />
            <Skeleton className="h-4 w-80" />
          </div>
          <div className="flex gap-3">
            <Skeleton className="h-10 w-36 rounded-lg" />
            <Skeleton className="h-10 w-36 rounded-lg" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => (
            <KPISkeleton key={i} />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Skeleton className="h-36 rounded-xl lg:col-span-2" />
          <Skeleton className="h-36 rounded-xl" />
        </div>
        <TableSkeleton rows={5} cols={6} />
      </div>
    );
  }

  /* ─── Error State ─── */
  if (error) {
    return (
      <div className="p-6 lg:p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center max-w-md mx-auto">
          <XCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-sm font-semibold text-red-800 mb-4">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const dashboard = dashboardData?.dashboard || {};
  const statusCountsRows = dashboard.status_counts || [];
  const recentOrders = dashboard.recent_orders || [];

  // { [order_status]: count } for the KPI cards
  const statusCounts: Record<string, number> = {};
  for (const row of statusCountsRows) {
    statusCounts[row.order_status] = Number(row.count) || 0;
  }

  // ORD-15: the server's figure excludes Cancelled. An older server sends no `totals`;
  // then sum every status except Cancelled here (the same rule).
  const orderedTonnage =
    dashboard.totals?.ordered_tonnage ??
    statusCountsRows
      .filter((row) => row.order_status !== 'Cancelled')
      .reduce((sum, row) => sum + (Number(row.total_tonnage) || 0), 0);
  const openTonnage = dashboard.totals ? dashboard.totals.open_tonnage : null;

  const priorityBreakdown: PriorityDatum[] = (dashboard.priority_counts || []).map((row) => ({
    priority: row.order_priority,
    count: Number(row.count) || 0,
  }));

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={Package}
        title="Order Management"
        subtitle="Track and manage material orders from creation through fulfillment"
        actions={
          <>
            <button
              type="button"
              onClick={() => navigate('/order-management/orders')}
              aria-label="All Orders"
              title="All Orders"
              className="inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 sm:px-3.5 border border-slate-300 bg-surface text-slate-700 hover:border-slate-400 hover:bg-slate-50 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <List className="w-4 h-4 text-slate-500" aria-hidden="true" />
              <span className="page-header__label">All Orders</span>
            </button>
            {canManage && (
              <button
                type="button"
                onClick={() => navigate('/order-management/orders/create')}
                aria-label="Create Order"
                title="Create Order"
                className="inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 sm:px-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              >
                <Plus className="w-4 h-4" aria-hidden="true" />
                <span className="page-header__label">Create Order</span>
              </button>
            )}
          </>
        }
      />

      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6">
        {/* ─── KPI Cards ─── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <KPICard
            icon={Activity}
            label="Active"
            value={statusCounts.Active || 0}
            accentColor="bg-primary-500"
            delay={0}
          />
          <KPICard
            icon={Clock}
            label="In Progress"
            value={statusCounts['In Progress'] || 0}
            accentColor="bg-amber-500"
            delay={60}
          />
          <KPICard
            icon={CheckCircle}
            label="Completed"
            value={statusCounts.Completed || 0}
            accentColor="bg-emerald-500"
            delay={120}
          />
          <KPICard
            icon={Pause}
            label="On Hold"
            value={statusCounts['On Hold'] || 0}
            accentColor="bg-slate-500"
            delay={180}
          />
        </div>

        {/* ─── Tonnage + Priority ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7">
            <TonnageCard orderedTonnage={orderedTonnage} openTonnage={openTonnage} delay={220} />
          </div>
          <div className="lg:col-span-5">
            <PriorityChart priorityData={priorityBreakdown} delay={280} />
          </div>
        </div>

        {/* ─── Recent Orders ─── */}
        <RecentOrdersTable orders={recentOrders} onViewOrder={handleViewOrder} delay={340} />
      </div>
    </div>
  );
};

export default OrderDashboard;
